// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Agents in Settings → Connections (design/agents-machine-settings A, JP 2026-11-04): a list of
// machines, then a page per machine with its Health, Access, Team and Accounts. Both take over the
// whole tab (LinkedAccountsTab); on phones the settings header carries the title and Back.

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {MachineAccountsSection} from '@app/features/konvo_agents/components/AgentAccountsSettings';
import {useNow} from '@app/features/konvo_agents/components/AgentBits';
import {MachineGlyph, OnlineDot} from '@app/features/konvo_agents/components/AgentBrand';
import {AgentMachineBrowser} from '@app/features/konvo_agents/components/AgentBrowserSettings';
import {
	AgentMachineDeviceAccess,
	showsDeviceAccessSetting,
} from '@app/features/konvo_agents/components/AgentDeviceAccessSettings';
import {AgentFollowUpSettings} from '@app/features/konvo_agents/components/AgentFollowUpSettings';
import {MachineHealthSection} from '@app/features/konvo_agents/components/AgentHealth';
import styles from '@app/features/konvo_agents/components/AgentMachineSettings.module.css';
import {AgentMergeRightsSettings} from '@app/features/konvo_agents/components/AgentMergeRightsSettings';
import {SettingsGroup, SettingsRow} from '@app/features/konvo_agents/components/AgentSettingsRows';
import {AgentMachineApprove, AgentMachineSharing} from '@app/features/konvo_agents/components/AgentShareSettings';
import {useAgentsOverview} from '@app/features/konvo_agents/components/AgentsConnection';
import {openAgentsConnectModal} from '@app/features/konvo_agents/components/AgentsConnectModal';
import {agentErrorMessage, offlineSince} from '@app/features/konvo_agents/components/AgentsCopy';
import {
	AgentMachineTerminals,
	showsTerminalsSetting,
} from '@app/features/konvo_agents/components/AgentTerminalsSettings';
import {canBrowse} from '@app/features/konvo_agents/state/AgentBrowser';
import AgentSettingsPage from '@app/features/konvo_agents/state/AgentSettingsPage';
import Agents from '@app/features/konvo_agents/state/Agents';
import {platformName, sharedProjects} from '@app/features/konvo_agents/state/AgentsModel';
import {DataMenuRenderer} from '@app/features/ui/action_menu/DataMenuRenderer';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {GlassMenu} from '@app/features/ui/glass_menu/GlassMenu';
import {StatusBadge} from '@app/features/ui/konvo_controls/KonvoControls';
import type {MenuGroupType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import type {AgentMachineView} from '@fluxer/schema/src/domains/konvo/Agents';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	IconArrowLeft,
	IconDots,
	IconFolders,
	IconLock,
	IconPencil,
	IconPlugConnectedX,
	IconPlus,
} from '@tabler/icons-react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';

function Back({label}: {label: React.ReactNode}) {
	// Phones go back from the settings header.
	if (MobileLayout.enabled) return null;
	return (
		<button
			type="button"
			className={styles.back}
			onClick={() => AgentSettingsPage.back()}
			data-flx="konvo-agents.manager.back"
		>
			<IconArrowLeft size={16} stroke={1.8} />
			{label}
		</button>
	);
}

const MachineStatus = observer(({machine, extra}: {machine: AgentMachineView; extra?: string}) => {
	const {i18n, t} = useLingui();
	const now = useNow(60_000);
	const platform = platformName(machine.platform);
	return (
		<span className={styles.status}>
			<OnlineDot online={machine.online} />
			<span className={styles.statusText}>
				{machine.online ? t`Online` : offlineSince(i18n, machine.lastSeenAt, now)}
				{platform && ` · ${platform}`}
				{extra && ` · ${extra}`}
			</span>
		</span>
	);
});

const MachineRow = observer(({machine}: {machine: AgentMachineView}) => {
	const {t} = useLingui();
	const state = machine.online ? machine.health?.state : undefined;
	return (
		<SettingsRow
			icon={<MachineGlyph platform={machine.platform} size={18} />}
			title={machine.name}
			description={<MachineStatus machine={machine} />}
			trailing={
				state === 'bad' ? (
					<StatusBadge
						state="bad"
						label={t({message: 'Problem', comment: 'Agents Settings: a machine has a problem.'})}
					/>
				) : state === 'warn' ? (
					<span className={styles.badge}>
						{t({
							message: 'Needs a look',
							comment: 'Agents Settings: a machine has something to look at, such as an update.',
						})}
					</span>
				) : undefined
			}
			onClick={() => AgentSettingsPage.open(machine.id)}
			data-flx="konvo-agents.machine.row"
			data-machine={machine.id}
		/>
	);
});

const MachineList = observer(() => {
	const {t} = useLingui();
	const machines = Agents.machines;
	return (
		<>
			<Back label={<Trans>Connections</Trans>} />
			<div className={styles.head}>
				<div className={styles.grow}>
					{!MobileLayout.enabled && (
						<h3 className={styles.title}>
							<Trans>Agents</Trans>
						</h3>
					)}
					<p className={styles.lead}>
						{t({
							message: 'Your machines and what Konvo can do on them',
							comment: 'Settings → Agents: under the title of the machine list.',
						})}
					</p>
				</div>
				<button
					type="button"
					className={styles.addMachine}
					onClick={openAgentsConnectModal}
					data-flx="konvo-agents.manager.add"
				>
					<IconPlus size={16} stroke={2} />
					<Trans>Add machine</Trans>
				</button>
			</div>
			{machines.length === 0 ? (
				<p className={styles.empty}>
					{!Agents.loaded ? (
						<Trans>Loading your machines…</Trans>
					) : Agents.error ? (
						<Trans>Konvo isn't answering right now, probably while it updates. Try again in a minute.</Trans>
					) : (
						<Trans>No machines connected yet.</Trans>
					)}
				</p>
			) : (
				<>
					<SettingsGroup
						title={t({message: 'Machines', comment: 'Settings → Agents: heading over the list of machines.'})}
					>
						{machines.map((machine) => (
							<MachineRow key={machine.id} machine={machine} />
						))}
					</SettingsGroup>
					{/* What Enter does with a message while an agent works: steer (default) or queue. */}
					<SettingsGroup
						title={t({
							message: 'All machines',
							comment: 'Settings → Agents: heading over settings that apply on every machine.',
						})}
					>
						<AgentFollowUpSettings />
					</SettingsGroup>
				</>
			)}
			{/* Staff: who may merge the agents' pull requests from Konvo (hidden for everyone else). */}
			<AgentMergeRightsSettings />
		</>
	);
});

const MachinePage = observer(({machine}: {machine: AgentMachineView}) => {
	const {i18n, t} = useLingui();
	const [editing, setEditing] = useState(false);
	const [name, setName] = useState(machine.name);
	const [saving, setSaving] = useState(false);
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
	const input = useRef<HTMLInputElement | null>(null);
	useEffect(() => {
		if (editing) input.current?.select();
	}, [editing]);
	const machineName = machine.name;
	const tool = machine.tools[0];
	const shared = sharedProjects(machine.limits, machine.projects);
	const projectCount = machine.limits.projects === 'all' ? machine.projects.length : machine.limits.projects.length;

	const save = async () => {
		const next = name.trim();
		if (!next || next === machine.name) {
			setEditing(false);
			setName(machine.name);
			return;
		}
		setSaving(true);
		try {
			await Agents.rename(machine.id, next);
			setEditing(false);
		} catch (error) {
			ToastCommands.error(agentErrorMessage(i18n, error));
		} finally {
			setSaving(false);
		}
	};

	const disconnect = () => {
		ModalCommands.push(
			ModalCommands.modal(() => (
				<ConfirmModal
					title={t`Disconnect ${machineName}?`}
					description={t`Konvo stops seeing this machine's agent chats straight away. The bridge on it stops at its next check. To connect it again, add it as a new machine.`}
					primaryText={t`Disconnect`}
					primaryVariant="danger"
					onPrimary={async () => {
						try {
							await Agents.disconnect(machine.id);
							AgentSettingsPage.open();
							ToastCommands.success(t`${machineName} disconnected.`);
						} catch (error) {
							ToastCommands.error(agentErrorMessage(i18n, error));
						}
					}}
				/>
			)),
		);
	};

	const menuGroups = (onClose: () => void): Array<MenuGroupType> => [
		{
			items: [
				{
					icon: <IconPencil size={16} stroke={1.7} />,
					label: t`Rename`,
					onClick: () => {
						onClose();
						setName(machine.name);
						setEditing(true);
					},
				},
				{
					icon: <IconPlugConnectedX size={16} stroke={1.7} />,
					label: t`Disconnect`,
					danger: true,
					onClick: () => {
						onClose();
						disconnect();
					},
				},
			],
		},
	];
	const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (MobileLayout.enabled) {
			setMenuAnchor(event.currentTarget);
			return;
		}
		ContextMenuCommands.openFromElementBottomRight(event, ({onClose}) => (
			<DataMenuRenderer groups={menuGroups(onClose)} />
		));
	};

	const projectsValue =
		shared.kind === 'all'
			? t({message: `All ${projectCount}`, comment: 'Agents Settings: Konvo sees all the projects on the machine.'})
			: shared.kind === 'none'
				? t({message: 'None yet', comment: 'Agents Settings: the machine shares no projects yet.'})
				: t({
						message: `${projectCount} shared`,
						comment: 'Agents Settings: how many projects the machine shares with Konvo.',
					});
	const projectNames =
		shared.kind === 'some'
			? `${shared.names.join(', ')}${shared.more > 0 ? ` ${t`+${shared.more} more`}` : ''}`
			: undefined;

	return (
		<div className={styles.page} data-flx="konvo-agents.machine">
			<Back label={<Trans>Agents</Trans>} />
			<div className={styles.head}>
				<span className={styles.tile}>
					<MachineGlyph platform={machine.platform} size={24} />
				</span>
				<div className={styles.grow}>
					{editing ? (
						<input
							ref={input}
							className={styles.nameInput}
							value={name}
							maxLength={80}
							disabled={saving}
							aria-label={t`Machine name`}
							onChange={(event) => setName(event.target.value)}
							onBlur={() => void save()}
							onKeyDown={(event) => {
								if (event.key === 'Enter') void save();
								if (event.key === 'Escape') {
									setName(machine.name);
									setEditing(false);
								}
							}}
							data-flx="konvo-agents.machine.name-input"
						/>
					) : (
						<h3 className={styles.title}>{machine.name}</h3>
					)}
					<MachineStatus machine={machine} extra={tool ? `${tool.name} ${tool.version}` : undefined} />
				</div>
				<button
					type="button"
					className={styles.more}
					onClick={openMenu}
					aria-label={t`More for ${machineName}`}
					data-flx="konvo-agents.machine.menu"
				>
					<IconDots size={18} stroke={2} />
				</button>
				{menuAnchor && (
					<GlassMenu
						isOpen
						onClose={() => setMenuAnchor(null)}
						anchor={menuAnchor}
						groups={menuGroups(() => setMenuAnchor(null))}
						data-flx="konvo-agents.machine.menu.glass"
					/>
				)}
			</div>
			{/* Health (checks and activity; docs/konvo-agents/HEALTH.md). */}
			<MachineHealthSection machine={machine} />
			<SettingsGroup title={t({message: 'Access', comment: 'Agents Settings: what Konvo can reach on the machine.'})}>
				{!machine.limits.send && (
					<SettingsRow
						icon={<IconLock size={18} stroke={1.7} />}
						title={t({
							message: 'Read only',
							comment: 'Agents Settings: the machine only shares its chats for reading.',
						})}
						description={t({
							message: 'Konvo can read chats here but not send. Change it with konvo-agents config there.',
							comment: 'Agents Settings: how a read-only machine works and where to change it.',
						})}
						data-flx="konvo-agents.machine.read-only"
					/>
				)}
				<SettingsRow
					icon={<IconFolders size={18} stroke={1.7} />}
					title={t({message: 'Projects', comment: 'Agents Settings: the projects Konvo sees on the machine.'})}
					description={
						projectNames ??
						t({message: 'What Konvo can see here', comment: 'Agents Settings: what the Projects row says.'})
					}
					trailing={<span className={styles.value}>{projectsValue}</span>}
					data-flx="konvo-agents.machine.projects"
				/>
				<AgentMachineApprove machine={machine} />
				{showsTerminalsSetting(machine) && <AgentMachineTerminals machine={machine} />}
				{/* The Agents browser: what it may reach through this machine, and who opened what. */}
				{machine.limits.send && (canBrowse(machine) || machine.limits.browserCap !== undefined) && (
					<AgentMachineBrowser machine={machine} />
				)}
				{/* Simulators: T3's Agent device access (on by default). */}
				{showsDeviceAccessSetting(machine) && <AgentMachineDeviceAccess machine={machine} />}
			</SettingsGroup>
			{/* Team threads: how far this machine lets its threads be shared, and what is shared now. */}
			<SettingsGroup
				title={t({message: 'Team', comment: 'Agents Settings: sharing the machine’s chats with teammates.'})}
				data-flx="konvo-agents.machine.sharing"
			>
				<AgentMachineSharing machine={machine} />
			</SettingsGroup>
			<MachineAccountsSection machine={machine} />
		</div>
	);
});

export const AgentsMachinesManager = observer(() => {
	const {t} = useLingui();
	useAgentsOverview(true);
	// Leaving Connections, or Settings, starts at Connections next time.
	useEffect(() => () => AgentSettingsPage.close(), []);
	const machine = Agents.machine(AgentSettingsPage.machineId);
	return (
		<section className={styles.manager} aria-label={t`Agents`} data-flx="konvo-agents.manager">
			{machine ? <MachinePage key={machine.id} machine={machine} /> : <MachineList />}
		</section>
	);
});
